import { Injectable } from '@angular/core';
import { GameTypePlugin } from '../interfaces/game-type-plugin.interface';

/**
 * Central registry for game type plugins.
 * Manages plugin registration, retrieval, and lifecycle.
 */
@Injectable({
  providedIn: 'root',
})
export class GamePluginRegistry {
  private plugins = new Map<string, GameTypePlugin>();

  /**
   * Register a game type plugin.
   * Throws an error if a plugin with the same ID is already registered.
   *
   * @param plugin - The plugin to register
   */
  register(plugin: GameTypePlugin): void {
    if (this.plugins.has(plugin.id)) {
      throw new Error(
        `Plugin with id "${plugin.id}" is already registered. ` +
          `Existing: ${this.plugins.get(plugin.id)?.name} v${this.plugins.get(plugin.id)?.version}, ` +
          `New: ${plugin.name} v${plugin.version}`
      );
    }

    console.log(
      `[GamePluginRegistry] Registered plugin: ${plugin.name} (${plugin.id}) v${plugin.version}`
    );
    this.plugins.set(plugin.id, plugin);
  }

  /**
   * Get a plugin by its ID.
   * Throws an error if the plugin is not found.
   *
   * @param id - The plugin ID
   * @returns The plugin instance
   */
  getPlugin(id: string): GameTypePlugin {
    const plugin = this.plugins.get(id);
    if (!plugin) {
      const availableIds = Array.from(this.plugins.keys()).join(', ');
      throw new Error(
        `Plugin with id "${id}" not found. Available plugins: ${availableIds || 'none'}`
      );
    }
    return plugin;
  }

  /**
   * Get all registered plugins.
   *
   * @returns Array of all plugin instances
   */
  getAllPlugins(): GameTypePlugin[] {
    return Array.from(this.plugins.values());
  }

  /**
   * Check if a plugin is registered.
   *
   * @param id - The plugin ID to check
   * @returns True if the plugin is registered
   */
  hasPlugin(id: string): boolean {
    return this.plugins.has(id);
  }

  /**
   * Unregister a plugin (primarily for testing).
   *
   * @param id - The plugin ID to unregister
   */
  unregister(id: string): void {
    if (this.plugins.delete(id)) {
      console.log(`[GamePluginRegistry] Unregistered plugin: ${id}`);
    }
  }

  /**
   * Clear all registered plugins (primarily for testing).
   */
  clear(): void {
    this.plugins.clear();
    console.log('[GamePluginRegistry] Cleared all plugins');
  }
}
